import React, { useState } from "react";
import { Box, Container, Typography, Grid, Divider } from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import YouTubeIcon from "@mui/icons-material/YouTube";
import TikTokIcon from "@mui/icons-material/MusicNote";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { useNavigate } from "react-router-dom";
import { useReveal } from "../hooks/useReveal";
import { keyframes } from "@mui/material";

const BLUE_LIGHT = "#42A5F5";
const NAVY      = "#2057aa";
const NAVY_MID  = "#073260";
const WHITE     = "#FFFFFF";
const MUTED     = "rgba(255,255,255,0.5)";
const BORDER    = "rgba(255,255,255,0.08)";

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;


const morphShape = keyframes`
  0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
  50%       { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
`;

const columns = [
  {
    title: "Courses",
    links: [
      { label: "All Courses", path: "/membership" },
      { label: "Foundation", path: "/membership/foundation" },
      { label: "Coaching", path: "/membership/coaching" },
      { label: "Classes", path: "/membership/classes" },
      { label: "Pricing", path: "/#pricing" },
    ],
  },
  {
    title: "Account",
    links: [
      { label: "Login", path: "/login" },
      { label: "My Dashboard", path: "/membership/Classes" },
      { label: "Profile", path: "/profile" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", path: "/privacy" },
      { label: "Terms of Service", path: "/terms" },
    ],
  },
];

const socials = [
  { icon: <InstagramIcon />, label: "Instagram", color: "#E1306C", href: "https://www.instagram.com/drpaw.lb" },
];

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const { ref, visible } = useReveal(0.05);
  const [hoveredSocial, setHoveredSocial] = useState<number | null>(null);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);

  const handleNav = (path: string) => {
    if (path.startsWith("/#")) {
      navigate("/");
      setTimeout(() => {
        const id = path.replace("/#", "");
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    } else {
      navigate(path);
    }
  };

  return (
    <Box ref={ref} sx={{
      background: `linear-gradient(160deg, ${NAVY} 0%, ${NAVY_MID} 60%, #0A1F38 100%)`,
      color: WHITE, position: "relative", overflow: "hidden",
    }}>

      {/* Background blobs */}
      <Box sx={{
        position: "absolute", top: "-20%", right: "-5%",
        width: 400, height: 400, borderRadius: "50%",
        background: `radial-gradient(circle, rgba(21,101,192,0.12) 0%, transparent 70%)`,
        animation: `${morphShape} 20s ease-in-out infinite`,
        pointerEvents: "none",
      }} />
      <Box sx={{
        position: "absolute", bottom: "-15%", left: "-8%",
        width: 300, height: 300, borderRadius: "50%",
        background: `radial-gradient(circle, rgba(66,165,245,0.08) 0%, transparent 70%)`,
        animation: `${morphShape} 25s ease-in-out infinite reverse`,
        pointerEvents: "none",
      }} />

      {/* Top wave */}
      <Box sx={{ lineHeight: 0, mb: "-1px" }}>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: "100%", height: "50px", display: "block" }}>
          <path d="M0,30 C360,60 1080,0 1440,30 L1440,0 L0,0 Z" fill="#FFFFFF" />
        </svg>
      </Box>

      <Container maxWidth="lg" sx={{ pt: 8, pb: 4, position: "relative", zIndex: 1 }}>

        {/* Main Grid */}
        <Grid container spacing={5}>

          {/* Brand Column */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(24px)",
              transition: "all 0.7s cubic-bezier(0.22,1,0.36,1) 0.15s",
            }}>
              {/* Logo */}
              <Box onClick={() => navigate("/")} sx={{ cursor: "pointer", mb: 3, display: "inline-flex" }}>
                <Box component="img" src="/logo.png" alt="Dr.Paw" sx={{ height: 64, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)" }} />
              </Box>

              <Typography sx={{ color: MUTED, lineHeight: 1.85, fontSize: "0.92rem", maxWidth: 300, mb: 4 }}>
                The leading digital platform for expert pet care courses and training.
                Join thousands of pet parents learning from top veterinary professionals.
              </Typography>

              {/* Contact info */}
              {[
                { icon: <LocationOnIcon sx={{ fontSize: "0.95rem" }} />, text: "Dbayeh, Lebanon" },
                { icon: <PhoneIcon sx={{ fontSize: "0.95rem" }} />, text: "04 541 965" },
                { icon: <EmailIcon sx={{ fontSize: "0.95rem" }} />, text: "hello@drpaw.com" },
              ].map((item, i) => (
                <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.5 }}>
                  <Box sx={{
                    color: BLUE_LIGHT, flexShrink: 0,
                    transition: "transform 0.2s ease",
                    "&:hover": { transform: "scale(1.15)" },
                  }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ color: MUTED, fontSize: "0.88rem" }}>{item.text}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Link Columns */}
          {columns.map((col, ci) => (
            <Grid key={col.title} size={{ xs: 6, sm: 4, md: 2 }}>
              <Box sx={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(24px)",
                transition: `all 0.7s cubic-bezier(0.22,1,0.36,1) ${0.2 + ci * 0.08}s`,
              }}>
                <Typography sx={{
                  fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.18em",
                  textTransform: "uppercase", color: BLUE_LIGHT, mb: 2.5,
                }}>
                  {col.title}
                </Typography>
                {col.links.map((link, li) => {
                  const key = `${ci}-${li}`;
                  return (
                    <Box
                      key={li}
                      onClick={() => handleNav(link.path)}
                      onMouseEnter={() => setHoveredLink(key)}
                      onMouseLeave={() => setHoveredLink(null)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 0.5,
                        mb: 1.5, cursor: "pointer",
                        color: hoveredLink === key ? WHITE : MUTED,
                        fontSize: "0.9rem",
                        transition: "all 0.25s ease",
                        transform: hoveredLink === key ? "translateX(6px)" : "translateX(0)",
                        animation: visible ? `${slideUp} 0.5s ease both` : "none",
                        animationDelay: `${0.25 + ci * 0.08 + li * 0.05}s`,
                      }}
                    >
                      {hoveredLink === key && (
                        <Box sx={{
                          width: 4, height: 4, borderRadius: "50%",
                          backgroundColor: BLUE_LIGHT, flexShrink: 0,
                        }} />
                      )}
                      {link.label}
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          ))}

          {/* Social Column */}
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <Box sx={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(24px)",
              transition: "all 0.7s cubic-bezier(0.22,1,0.36,1) 0.42s",
            }}>
              <Typography sx={{
                fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.18em",
                textTransform: "uppercase", color: BLUE_LIGHT, mb: 2.5,
              }}>
                Follow Us
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.2 }}>
                {socials.map((s, i) => (
                  <Box
                    key={i}
                    onMouseEnter={() => setHoveredSocial(i)}
                    onMouseLeave={() => setHoveredSocial(null)}
                    onClick={() => window.open(s.href, "_blank", "noopener,noreferrer")}
                    sx={{
                      width: 42, height: 42, borderRadius: "12px",
                      border: `1px solid ${hoveredSocial === i ? s.color : BORDER}`,
                      backgroundColor: hoveredSocial === i ? `${s.color}22` : "rgba(255,255,255,0.04)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)",
                      transform: hoveredSocial === i ? "scale(1.15) translateY(-3px)" : "scale(1)",
                      boxShadow: hoveredSocial === i ? `0 6px 20px ${s.color}44` : "none",
                      "& .MuiSvgIcon-root": {
                        fontSize: "1.1rem",
                        color: hoveredSocial === i ? s.color : MUTED,
                        transition: "color 0.25s ease",
                      },
                      animation: visible ? `${slideUp} 0.5s ease both` : "none",
                      animationDelay: `${0.45 + i * 0.07}s`,
                    }}
                  >
                    {s.icon}
                  </Box>
                ))}
              </Box>
            </Box>
          </Grid>

        </Grid>

        {/* Divider */}
        <Divider sx={{ my: 6, borderColor: BORDER }} />

        {/* Bottom Bar */}
        <Box sx={{
          display: "flex", justifyContent: "space-between",
          alignItems: "center", flexWrap: "wrap", gap: 2,
          opacity: visible ? 1 : 0,
          transition: "opacity 0.8s ease 0.55s",
        }}>
          <Typography sx={{ color: MUTED, fontSize: "0.82rem" }}>
            © {new Date().getFullYear()} Dr.Paw. All rights reserved.
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{
              width: 8, height: 8, borderRadius: "50%", backgroundColor: "#4CAF50",
              boxShadow: "0 0 8px #4CAF50",
              animation: "pulse-green 1.8s ease-in-out infinite",
              "@keyframes pulse-green": {
                "0%, 100%": { opacity: 1, transform: "scale(1)" },
                "50%": { opacity: 0.6, transform: "scale(0.75)" },
              },
            }} />
            <Typography sx={{ color: MUTED, fontSize: "0.82rem" }}>
              All systems operational
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 3 }}>
            {["Privacy", "Terms"].map((item) => (
              <Typography
                key={item}
                onClick={() => navigate(`/${item.toLowerCase()}`)}
                sx={{
                  color: MUTED, fontSize: "0.82rem", cursor: "pointer",
                  transition: "color 0.2s ease",
                  "&:hover": { color: WHITE },
                }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>

      </Container>
    </Box>
  );
};

export default Footer;
